import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Archive,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Database,
  Search,
  RefreshCw,
  Loader2,
  HardDrive,
  ShieldAlert,
  AlertOctagon,
  Calendar,
  Info,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { CustomDropdown } from '../../../components/CustomDropdown ';

interface MSSQLBackupViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

interface BackupDatabaseSummary {
  database: string;
  is_system: boolean;
  recovery_model: string;
  last_full_at: string | null;
  last_diff_at: string | null;
  last_log_at: string | null;
  hours_since_full: number | null;
  hours_since_diff: number | null;
  hours_since_log: number | null;
  backups_in_window: number;
  backup_size_mb_in_window: number;
  by_type?: Record<string, any>;
  flags?: string[];
  needs_attention: boolean;
}

interface BackupTotals {
  databases?: number;
  needing_attention?: number;
  never_backed_up?: number;
  full_backup_stale?: number;
  log_backup_missing?: number;
  damaged_backup?: number;
  backups_in_window?: number;
  backup_size_mb_in_window?: number;
}

interface BackupThresholds {
  stale_full_hours?: number;
  stale_log_hours?: number;
}

interface BackupApiResponse {
  status: boolean;
  applicable?: boolean;
  capability?: {
    mechanism?: string;
    status?: string;
  };
  data?: any[];
  total?: number;
  summary?: {
    databases?: BackupDatabaseSummary[];
    totals?: BackupTotals;
    by_type?: Record<string, any>;
    thresholds?: BackupThresholds;
  };
}

export const MSSQLBackupView: React.FC<MSSQLBackupViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [backupData, setBackupData] = useState<BackupApiResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'attention' | 'healthy' | 'never'>('all');
  const [filterType, setFilterType] = useState<'all' | 'user' | 'system'>('all');
  const [filterRecovery, setFilterRecovery] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'databases' | 'history'>('databases');

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);
    try {
      const params: any = { cluster_id: activeCluster, minutes: timeRange };
      if (clientId) params.client_id = clientId;

      const res = await baseapi.get('/db_monitor_dashboard/backup/mssql', { params });
      setBackupData(res.data);
    } catch (err) {
      console.warn('Failed to fetch MSSQL Backup metrics:', err);
      setBackupData(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const summary = backupData?.summary;
  const totals = summary?.totals || {};
  const databases = summary?.databases || [];
  const thresholds = summary?.thresholds || { stale_full_hours: 168, stale_log_hours: 24 };
  const capability = backupData?.capability || {};
  const historyLogs = Array.isArray(backupData?.data) ? backupData!.data : [];

  // Filter Options for CustomDropdown
  const statusOptions = useMemo(
    () => [
      { label: 'All Statuses', value: 'all' },
      { label: 'Needs Attention', value: 'attention' },
      { label: 'Compliant / Healthy', value: 'healthy' },
      { label: 'Never Backed Up', value: 'never' },
    ],
    []
  );

  const typeOptions = useMemo(
    () => [
      { label: 'All Types', value: 'all' },
      { label: 'User DBs', value: 'user' },
      { label: 'System DBs', value: 'system' },
    ],
    []
  );

  const recoveryOptions = useMemo(
    () => [
      { label: 'All Models', value: 'all' },
      { label: 'FULL', value: 'FULL' },
      { label: 'SIMPLE', value: 'SIMPLE' },
      { label: 'BULK LOGGED', value: 'BULK_LOGGED' },
    ],
    []
  );

  // Filter & Search Logic for Databases
  const filteredDatabases = useMemo(() => {
    return databases.filter((db) => {
      // Search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (!db.database.toLowerCase().includes(q)) return false;
      }

      // Status filter
      if (filterStatus === 'attention' && !db.needs_attention) return false;
      if (filterStatus === 'healthy' && db.needs_attention) return false;
      if (filterStatus === 'never' && (!db.flags || !db.flags.includes('never_backed_up'))) return false;

      // Type filter
      if (filterType === 'user' && db.is_system) return false;
      if (filterType === 'system' && !db.is_system) return false;

      // Recovery model filter
      if (filterRecovery !== 'all' && db.recovery_model?.toUpperCase() !== filterRecovery.toUpperCase()) {
        return false;
      }

      return true;
    });
  }, [databases, searchQuery, filterStatus, filterType, filterRecovery]);

  const formatHours = (hours: number | null | undefined) => {
    if (hours == null) return null;
    if (hours < 1) return '< 1 hour ago';
    if (hours < 24) return `${Math.round(hours)}h ago`;
    const days = Math.floor(hours / 24);
    const remHours = Math.round(hours % 24);
    if (remHours === 0) return `${days}d ago`;
    return `${days}d ${remHours}h ago`;
  };

  const formatDateTime = (iso: string | null | undefined) => {
    if (!iso) return 'Never';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return 'Never';
      return d.toLocaleString([], {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const formatBytesMb = (mb: number | null | undefined) => {
    if (mb == null || mb === 0) return '0 MB';
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(2)} MB`;
  };

  const renderFlagBadge = (flag: string) => {
    switch (flag) {
      case 'never_backed_up':
        return (
          <span
            key={flag}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-rose-500/20 text-rose-400 border border-rose-500/40"
            title="Database has never been backed up"
          >
            <AlertOctagon className="w-3 h-3 shrink-0" />
            Never Backed Up
          </span>
        );
      case 'full_backup_stale':
        return (
          <span
            key={flag}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/40"
            title={`Full backup exceeds stale threshold (${thresholds.stale_full_hours || 168}h)`}
          >
            <Clock className="w-3 h-3 shrink-0" />
            Full Stale
          </span>
        );
      case 'log_backup_missing':
        return (
          <span
            key={flag}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-orange-500/20 text-orange-400 border border-orange-500/40"
            title={`Log backup missing or exceeds threshold (${thresholds.stale_log_hours || 24}h) on FULL recovery model`}
          >
            <ShieldAlert className="w-3 h-3 shrink-0" />
            Log Missing
          </span>
        );
      case 'damaged_backup':
        return (
          <span
            key={flag}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-600/30 text-red-300 border border-red-500/50"
            title="Damaged backup media or integrity warning reported"
          >
            <AlertTriangle className="w-3 h-3 shrink-0" />
            Damaged
          </span>
        );
      default:
        return (
          <span
            key={flag}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-gray-800 text-gray-300 border border-gray-700"
          >
            {flag.replace(/_/g, ' ')}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/60 backdrop-blur-[2px] z-20 flex items-center justify-center rounded-xl min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">Loading MSSQL backup telemetry...</span>
        </div>
      </div>
    );
  }

  const attentionCount = totals.needing_attention ?? databases.filter((d) => d.needs_attention).length;
  const neverBackedUpCount = totals.never_backed_up ?? databases.filter((d) => d.flags?.includes('never_backed_up')).length;
  const fullStaleCount = totals.full_backup_stale ?? databases.filter((d) => d.flags?.includes('full_backup_stale')).length;
  const logMissingCount = totals.log_backup_missing ?? databases.filter((d) => d.flags?.includes('log_backup_missing')).length;
  const totalDbs = totals.databases ?? databases.length;

  return (
    <div className="space-y-4">
      {/* Top Header Card */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded bg-orange-500 text-white">
            <Archive className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-gray-900 dark:text-white tracking-wide">MSSQL Backup & Recovery Health</h2>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30 font-semibold uppercase">
                {capability.mechanism || 'msdb'}
              </span>
              {capability.status && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30 font-semibold">
                  {capability.status.replace(/_/g, ' ')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Global Alert Status Pill */}
        <div className="flex items-center gap-2.5 shrink-0 font-mono text-xs">
          {attentionCount > 0 ? (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rose-50 dark:bg-rose-500/15 border border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-300">
              <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400 animate-pulse" />
              <span className="font-bold">{attentionCount} DBs Require Attention</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/15 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span className="font-bold">All Backups Compliant</span>
            </div>
          )}

          <button
            onClick={fetchData}
            className="p-1.5 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-700 rounded-lg transition-colors cursor-pointer shadow-sm"
            title="Refresh Backups"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Total Databases */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <Database className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
              Databases
            </span>
          </div>
          <div className="mt-1">
            <div className="text-xl font-bold font-mono text-gray-900 dark:text-white">{totalDbs}</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
              {databases.filter((d) => !d.is_system).length} User / {databases.filter((d) => d.is_system).length} System
            </div>
          </div>
        </div>

        {/* Needs Attention */}
        <div
          className={`border rounded-xl p-3.5 shadow-sm dark:shadow-md flex flex-col justify-between transition-all ${attentionCount > 0
            ? 'bg-rose-50/70 dark:bg-rose-950/20 border-rose-200 dark:border-rose-500/40 hover:border-rose-300 dark:hover:border-rose-500/60'
            : 'bg-white dark:bg-gray-800/40 border-gray-200 dark:border-gray-700/60'
            }`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <AlertTriangle className={`w-3.5 h-3.5 ${attentionCount > 0 ? 'text-rose-500 dark:text-rose-400' : 'text-gray-400'}`} />
              Attention
            </span>
            {attentionCount > 0 && (
              <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 font-bold border border-rose-200 dark:border-rose-500/30">
                Action Req.
              </span>
            )}
          </div>
          <div className="mt-1">
            <div className={`text-xl font-bold font-mono ${attentionCount > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {attentionCount}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Non-compliant databases</div>
          </div>
        </div>

        {/* Never Backed Up */}
        <div
          className={`border rounded-xl p-3.5 shadow-sm dark:shadow-md flex flex-col justify-between ${neverBackedUpCount > 0 ? 'bg-red-50/70 dark:bg-red-950/20 border-red-200 dark:border-red-500/30' : 'bg-white dark:bg-gray-800/40 border-gray-200 dark:border-gray-700/60'
            }`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <AlertOctagon className={`w-3.5 h-3.5 ${neverBackedUpCount > 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-400'}`} />
              Never Backed
            </span>
          </div>
          <div className="mt-1">
            <div className={`text-xl font-bold font-mono ${neverBackedUpCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {neverBackedUpCount}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">No backup record found</div>
          </div>
        </div>

        {/* Full Backup Stale */}
        <div
          className={`border rounded-xl p-3.5 shadow-sm dark:shadow-md flex flex-col justify-between ${fullStaleCount > 0 ? 'bg-amber-50/70 dark:bg-amber-950/20 border-amber-200 dark:border-amber-500/30' : 'bg-white dark:bg-gray-800/40 border-gray-200 dark:border-gray-700/60'
            }`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <Clock className={`w-3.5 h-3.5 ${fullStaleCount > 0 ? 'text-amber-500 dark:text-amber-400' : 'text-gray-400'}`} />
              Full Stale
            </span>
          </div>
          <div className="mt-1">
            <div className={`text-xl font-bold font-mono ${fullStaleCount > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {fullStaleCount}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">&gt; {thresholds.stale_full_hours || 168}h since Full</div>
          </div>
        </div>

        {/* Log Backup Missing */}
        <div
          className={`border rounded-xl p-3.5 shadow-sm dark:shadow-md flex flex-col justify-between ${logMissingCount > 0 ? 'bg-orange-50/70 dark:bg-orange-950/20 border-orange-200 dark:border-orange-500/30' : 'bg-white dark:bg-gray-800/40 border-gray-200 dark:border-gray-700/60'
            }`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <ShieldAlert className={`w-3.5 h-3.5 ${logMissingCount > 0 ? 'text-orange-500 dark:text-orange-400' : 'text-gray-400'}`} />
              Log Missing
            </span>
          </div>
          <div className="mt-1">
            <div className={`text-xl font-bold font-mono ${logMissingCount > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {logMissingCount}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">&gt; {thresholds.stale_log_hours || 24}h on FULL DBs</div>
          </div>
        </div>

        {/* Backups in Window */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <HardDrive className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
              Window Activity
            </span>
          </div>
          <div className="mt-1">
            <div className="text-xl font-bold font-mono text-emerald-600 dark:text-emerald-400">
              {totals.backups_in_window ?? 0} <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">runs</span>
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
              Size: {formatBytesMb(totals.backup_size_mb_in_window ?? 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl shadow-sm dark:shadow-xl overflow-hidden">
        {/* Controls & Search Toolbar */}
        <div className="p-3.5 border-b border-gray-200 dark:border-gray-700/60 flex flex-col lg:flex-row lg:items-center justify-between gap-3 bg-gray-50 dark:bg-gray-900/60">
          {/* Sub Tab Switcher */}
          <div className="flex items-center gap-1 bg-gray-200/80 dark:bg-gray-950/80 p-1 rounded-lg border border-gray-300/80 dark:border-gray-800 shrink-0">
            <button
              onClick={() => setActiveTab('databases')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${activeTab === 'databases'
                ? 'bg-orange-600 text-white shadow-md'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/50'
                }`}
            >
              <Database className="w-3.5 h-3.5" />
              Database Health ({filteredDatabases.length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${activeTab === 'history'
                ? 'bg-orange-600 text-white shadow-md'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/50'
                }`}
            >
              <Calendar className="w-3.5 h-3.5" />
              Backup History Logs ({historyLogs.length})
            </button>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-wrap items-center gap-2 flex-1 justify-end">
            {/* Search Input */}
            <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search database..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white dark:bg-gray-900/80 border border-gray-300 dark:border-gray-700/80 rounded-lg pl-8 pr-3 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors shadow-sm"
              />
            </div>

            {/* Status Filter */}
            <CustomDropdown
              data={statusOptions}
              selectedValue={filterStatus}
              onSelect={(val) => setFilterStatus(val as any)}
              width="w-40"
              placeholder="All Statuses"
              buttonHeight={8}
            />

            {/* DB Type Filter */}
            <CustomDropdown
              data={typeOptions}
              selectedValue={filterType}
              onSelect={(val) => setFilterType(val as any)}
              width="w-36"
              placeholder="All Types"
              buttonHeight={8}
            />

            {/* Recovery Model Filter */}
            <CustomDropdown
              data={recoveryOptions}
              selectedValue={filterRecovery}
              onSelect={(val) => setFilterRecovery(val)}
              width="w-40"
              placeholder="All Models"
              buttonHeight={8}
            />
          </div>
        </div>

        {/* Tab 1: Database Health Overview Table */}
        {activeTab === 'databases' && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700/80 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest bg-gray-50 dark:bg-gray-900/90">
                  <th className="py-3 px-3.5">DATABASE</th>
                  <th className="py-3 px-3.5">RECOVERY MODEL</th>
                  <th className="py-3 px-3.5">LAST FULL BACKUP</th>
                  <th className="py-3 px-3.5">LAST DIFF BACKUP</th>
                  <th className="py-3 px-3.5">LAST LOG BACKUP</th>
                  <th className="py-3 px-3.5 text-right">WINDOW ACTIVITY</th>
                  <th className="py-3 px-3.5 text-left">FLAGS & COMPLIANCE</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800/60 font-mono text-xs">
                {filteredDatabases.length > 0 ? (
                  filteredDatabases.map((db, _idx) => {
                    const isFullStale = db.flags?.includes('full_backup_stale');
                    const isLogMissing = db.flags?.includes('log_backup_missing');

                    return (
                      <tr
                        key={db.database}
                        className={`hover:bg-orange-500/[0.04] transition-colors ${db.needs_attention ? 'bg-rose-50/40 dark:bg-rose-950/[0.07]' : ''
                          }`}
                      >
                        {/* Database Name & System Tag */}
                        <td className="py-3 px-3.5">
                          <div className="flex items-center gap-2">
                            <Database className={`w-4 h-4 shrink-0 ${db.is_system ? 'text-gray-400' : 'text-blue-500 dark:text-blue-400'}`} />
                            <div>
                              <span className="font-bold text-gray-900 dark:text-white tracking-wide font-sans">{db.database}</span>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span
                                  className={`text-[9px] px-1.5 py-0.2 rounded font-bold uppercase ${db.is_system
                                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'
                                    : 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20'
                                    }`}
                                >
                                  {db.is_system ? 'System' : 'User DB'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Recovery Model */}
                        <td className="py-3 px-3.5">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${db.recovery_model === 'FULL'
                              ? 'bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-500/30'
                              : db.recovery_model === 'SIMPLE'
                                ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                              }`}
                          >
                            {db.recovery_model || 'UNKNOWN'}
                          </span>
                        </td>

                        {/* Last Full Backup */}
                        <td className="py-3 px-3.5">
                          {db.last_full_at ? (
                            <div>
                              <div className="text-gray-900 dark:text-gray-200">{formatDateTime(db.last_full_at)}</div>
                              <div className="mt-0.5">
                                <span
                                  className={`text-[10px] px-1.5 py-0.2 rounded font-semibold ${isFullStale
                                    ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'
                                    }`}
                                >
                                  {formatHours(db.hours_since_full) || 'Recent'}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-rose-600 dark:text-rose-400 font-bold bg-rose-50 dark:bg-rose-500/10 px-2 py-0.5 rounded border border-rose-200 dark:border-rose-500/20 text-[11px]">
                              Never
                            </span>
                          )}
                        </td>

                        {/* Last Diff Backup */}
                        <td className="py-3 px-3.5">
                          {db.last_diff_at ? (
                            <div>
                              <div className="text-gray-900 dark:text-gray-200">{formatDateTime(db.last_diff_at)}</div>
                              <div className="mt-0.5">
                                <span className="text-[10px] px-1.5 py-0.2 rounded font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400">
                                  {formatHours(db.hours_since_diff) || 'Recent'}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 text-[11px]">—</span>
                          )}
                        </td>

                        {/* Last Log Backup */}
                        <td className="py-3 px-3.5">
                          {db.recovery_model === 'SIMPLE' ? (
                            <span className="text-gray-400 dark:text-gray-500 text-[11px] italic" title="Not applicable for SIMPLE recovery model">
                              N/A (Simple)
                            </span>
                          ) : db.last_log_at ? (
                            <div>
                              <div className="text-gray-900 dark:text-gray-200">{formatDateTime(db.last_log_at)}</div>
                              <div className="mt-0.5">
                                <span
                                  className={`text-[10px] px-1.5 py-0.2 rounded font-semibold ${isLogMissing
                                    ? 'bg-orange-100 dark:bg-orange-500/20 text-orange-800 dark:text-orange-300 border border-orange-200 dark:border-orange-500/30'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'
                                    }`}
                                >
                                  {formatHours(db.hours_since_log) || 'Recent'}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-orange-600 dark:text-orange-400 font-bold bg-orange-50 dark:bg-orange-500/10 px-2 py-0.5 rounded border border-orange-200 dark:border-orange-500/20 text-[11px]">
                              Missing Log
                            </span>
                          )}
                        </td>

                        {/* Window Activity */}
                        <td className="py-3 px-3.5 text-right">
                          <div className="text-gray-900 dark:text-gray-200 font-semibold">{db.backups_in_window || 0} runs</div>
                          <div className="text-[10px] text-gray-500 dark:text-gray-400">
                            {formatBytesMb(db.backup_size_mb_in_window || 0)}
                          </div>
                        </td>

                        {/* Flags & Compliance Status */}
                        <td className="py-3 px-3.5">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {db.flags && db.flags.length > 0 ? (
                              db.flags.map((flag) => renderFlagBadge(flag))
                            ) : db.needs_attention ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-rose-50 dark:bg-rose-500/20 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-500/40">
                                Needs Attention
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">
                                <CheckCircle2 className="w-3 h-3" />
                                Healthy
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-xs text-gray-500 dark:text-gray-400 italic">
                      No databases match the selected filters or search criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Tab 2: Backup History Logs */}
        {activeTab === 'history' && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700/80 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest bg-gray-50 dark:bg-gray-900/90">
                  <th className="py-3 px-3.5">TIMESTAMP / START</th>
                  <th className="py-3 px-3.5">DATABASE</th>
                  <th className="py-3 px-3.5">TYPE</th>
                  <th className="py-3 px-3.5 text-right">SIZE (MB)</th>
                  <th className="py-3 px-3.5 text-right">COMPRESSED</th>
                  <th className="py-3 px-3.5">DEVICE / LOCATION</th>
                  <th className="py-3 px-3.5 text-right">STATUS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800/60 font-mono text-xs">
                {historyLogs.length > 0 ? (
                  historyLogs.map((log: any, idx: number) => {
                    const dbName = log.database_name || log.database || '—';
                    const bType = log.type || log.backup_type || 'Full';
                    const isDamaged = log.is_damaged === true;
                    return (
                      <tr key={idx} className="hover:bg-orange-500/[0.04] transition-colors">
                        <td className="py-3 px-3.5 text-gray-900 dark:text-gray-200">
                          {formatDateTime(log.backup_start_date || log.start_time || log.timestamp)}
                        </td>
                        <td className="py-3 px-3.5 font-bold text-gray-900 dark:text-white font-sans">{dbName}</td>
                        <td className="py-3 px-3.5">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20">
                            {bType}
                          </span>
                        </td>
                        <td className="py-3 px-3.5 text-right text-gray-900 dark:text-gray-200">
                          {formatBytesMb(log.backup_size_mb || log.size_mb || 0)}
                        </td>
                        <td className="py-3 px-3.5 text-right text-gray-600 dark:text-gray-400">
                          {formatBytesMb(log.compressed_backup_size_mb || log.compressed_size_mb || 0)}
                        </td>
                        <td className="py-3 px-3.5 text-gray-600 dark:text-gray-400 max-w-xs truncate" title={log.physical_device_name || log.location}>
                          {log.physical_device_name || log.location || '—'}
                        </td>
                        <td className="py-3 px-3.5 text-right">
                          {isDamaged ? (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-50 dark:bg-rose-500/20 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-500/40">
                              Damaged
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">
                              Success
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <Archive className="w-8 h-8 text-gray-400 dark:text-gray-600" />
                        <span className="font-medium text-gray-700 dark:text-gray-300">No backup events recorded in the selected time window.</span>
                        <span className="text-[11px] text-gray-500">
                          Widen the time range filter or verify if MSSQL Agent backup jobs are scheduled.
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer Notes & SLA Thresholds Info */}
        <div className="p-3 bg-gray-50 dark:bg-gray-900/80 border-t border-gray-200 dark:border-gray-700/60 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[11px] text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-2">
            <Info className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400 shrink-0" />
            <span>
              <strong>Full Stale Threshold:</strong> {thresholds.stale_full_hours || 168}h (7 days) &nbsp;|&nbsp;{' '}
              <strong>Log Missing Threshold:</strong> {thresholds.stale_log_hours || 24}h (1 day for FULL recovery model)
            </span>
          </div>
          <div className="font-mono text-gray-500">
            Source: <code className="text-gray-800 dark:text-gray-300 font-bold">msdb.dbo.backupset</code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MSSQLBackupView;
